import styles from "./ProfileImage.module.css";
import cx from "classnames";
import { CSSProperties } from "react";
import { HTMLMotionProps, motion } from "motion/react";
import { ReactComponent as TickedIcon } from "@jnctn/icons/ticked.svg";
import { ReactComponent as CrossedIcon } from "@jnctn/icons/crossed.svg";
import { ProfileImageProps } from "@jnctn/bridge-design-system/types/ProfileImage";

export type { ProfileImageProps };

function getImageUrl(
  excludeImgPrefix?: boolean,
  url?: string
): string | undefined {
  if (!excludeImgPrefix && url) {
    return `url(data:image/png;base64,${url})`;
  }
  return url ? `url(${url})` : undefined;
}

const variants = {
  // eslint-disable-next-line no-magic-numbers
  shake: { x: [0, -20, 20, 0], transition: { duration: 0.25 } },
  // eslint-disable-next-line no-magic-numbers
  subtlegrow: { scale: [0.95, 1], transition: { duration: 0.4 } },
  grow: { scale: [0, 1], transition: { duration: 0.4 } },
};


type WebProfileImageProps = Omit<ProfileImageProps, "outline"> &
  HTMLMotionProps<"div"> & {
    style?: CSSProperties;
    excludeImgPrefix?: boolean;
    animate?: keyof typeof variants | false;
    animateIcon?: keyof typeof variants | false;
    outline?: boolean; // now optional
  };


export default function ProfileImage({
  subject,
  element,
  children,
  icon,
  className,
  size,
  style,
  outline = false,
  excludeImgPrefix,
  animate,
  animateIcon,
  ...motionProps
}: WebProfileImageProps) {
  return (
    <div className={style ? undefined : "relative"} style={style}>
      <motion.div
        className={cx(styles.profile, {
          [styles.xxsmall]: size === "xxsmall",
          [styles.xsmall]: size === "xsmall",
          [styles.small]: size === "small",
          [styles.medium]: size === "medium",
          [styles.large]: size === "large",
          [styles.xlarge]: size === "xlarge",
          [styles.outline]: outline,
        })}
        variants={variants}
        animate={animate !== false ? animate : undefined}
        {...motionProps}
      >
        <div
          role="img"
          aria-label="Profile image"
          className={cx("rounded", styles.profileImage, className, {
            [styles.noProfile]: subject && subject.image === undefined,
            [styles.loading]: subject === undefined,
          })}
          style={
            subject
              ? {
                  backgroundImage: getImageUrl(excludeImgPrefix, subject.image),
                }
              : undefined
          }
        />
      </motion.div>
      {icon !== undefined ? (
        <motion.div
          className={cx("absolute z-50", {
            "right-0 bottom-0": size === "xsmall" || size === "small",
            "right-1 bottom-1": size === "medium",
            "right-2 bottom-2": size === "large" || size === "xlarge",
          })}
          variants={variants}
          animate={
            animate === undefined &&
            animateIcon === undefined &&
            icon === "ticked"
              ? "grow"
              : animate !== false && animateIcon !== false
              ? animateIcon || animate
              : undefined
          }
        >
          {icon === "ticked" ? (
            <TickedIcon
              className={cx({
                "w-4 h-4": size === "xxsmall",
                "w-10 h-10": size === "xsmall",
                "w-12 h-12": size === "small",
                "w-14 h-14": size === "medium",
                "w-16 h-16": size === "large" || size === "xlarge",
              })}
            />
          ) : (
            <CrossedIcon
              className={cx({
                "w-4 h-4": size === "xxsmall",
                "w-10 h-10": size === "xsmall",
                "w-12 h-12": size === "small",
                "w-14 h-14": size === "medium",
                "w-16 h-16": size === "large" || size === "xlarge",
              })}
            />
          )}
        </motion.div>
      ) : null}
    </div>
  );
}

